import { memo, useMemo } from "react";
import { TreeSelect, DropdownSkeleton } from "@atlas-int/ui";
import {
  buildLocationTree,
  getInitialExpandedLocationIds,
  locationSearchFunction,
  getLabelForLocation,
  LocationItem,
} from "./LocationDropdownUtils";
import LocationDropdownItemMUI from "./LocationDropdownItemMUI";

interface LocationDropdownProps {
  /** Flat list of location items to display */
  locations: LocationItem[];
  /** Currently selected location ID */
  value: string | undefined;
  /** Called when the user selects a location */
  onChange: (id: string) => void;
  /** Whether regions/subregions/world should be non-selectable (only expandable) */
  disableRegions?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Error state */
  error?: boolean;
  /** Optional icon source for the world item */
  worldIconSrc?: string;
  /** Accessible name for the combobox (e.g. "Exporter", "Importer"). */
  ariaLabel?: string;
}

const LocationDropdown = ({
  locations,
  value,
  onChange,
  disableRegions = false,
  loading = false,
  error = false,
  worldIconSrc,
  ariaLabel,
}: LocationDropdownProps) => {
  // Find selected location
  const selectedLocation = useMemo(() => {
    return locations.find((item) => item.id === value);
  }, [locations, value]);

  // Get initially expanded IDs
  const initialExpandedIds = useMemo(() => {
    return getInitialExpandedLocationIds(locations, selectedLocation);
  }, [locations, selectedLocation]);

  // Select handler
  const handleSelect = (item: LocationItem) => {
    onChange(item.id);
  };

  if (loading) {
    return <DropdownSkeleton />;
  }

  if (error) {
    return <DropdownSkeleton />;
  }

  if (locations.length === 0) {
    return <DropdownSkeleton />;
  }

  return (
    <TreeSelect
      items={locations}
      buildTree={buildLocationTree}
      value={selectedLocation}
      onChange={handleSelect}
      placeholder="Please select a location"
      ariaLabel={ariaLabel}
      getLabel={getLabelForLocation}
      searchFilter={locationSearchFunction}
      renderItem={(props) => (
        <LocationDropdownItemMUI
          {...props}
          disableRegions={disableRegions}
          worldIconSrc={worldIconSrc}
        />
      )}
      estimatedItemHeight={34}
      defaultExpandedIds={initialExpandedIds}
      showParentHierarchyOnSearch={false}
      minWidth="320px"
      maxHeight="300px"
    />
  );
};

export default memo(LocationDropdown);
